import React from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, Box, TextField } from '@mui/material';
import styles from '../../../../../index.module.scss';

export type Dimension = { Id: string; Name: string };
export type DimensionOptions = {
  locations: Dimension[];
  classes: Dimension[];
  locationsEnabled: boolean;
  locationLabel?: string;
  classesEnabled: boolean;
};

type Props = {
  prefix: 'reference' | 'debit' | 'credit';
  control: any;
  options: DimensionOptions | null;
  readOnly: boolean;
  loading: boolean;
};

export default function DimensionFields({ prefix, control, options, readOnly, loading }: Props) {
  return (
    <Box sx={{ mb: 2, pl: 2 }}>
      {(['Location', 'Class'] as const).map(kind => {
        const values = (kind === 'Location' ? options?.locations : options?.classes) || [];
        const enabled = kind === 'Location' ? options?.locationsEnabled : options?.classesEnabled;
        if (enabled === false) return null;
        const kindLabel = kind === 'Location' ? options?.locationLabel?.trim() || 'Location' : 'Class';
        const label = `${prefix[0].toUpperCase()}${prefix.slice(1)} ${kindLabel}`;
        return (
          <Controller
            key={kind}
            name={`${prefix}${kind}`}
            control={control}
            shouldUnregister={false}
            render={({ field }) => (
              <Autocomplete
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                onBlur={field.onBlur}
                options={values}
                getOptionLabel={(value: Dimension) => value.Name}
                isOptionEqualToValue={(option, value) => option.Id === value.Id}
                readOnly={readOnly}
                disabled={readOnly || loading || !options}
                forcePopupIcon={!readOnly}
                loading={loading}
                noOptionsText={enabled ? 'No active values in QuickBooks' : 'Tracking is disabled in QuickBooks'}
                sx={{ my: 1 }}
                renderInput={params => (
                  <TextField
                    {...params}
                    inputRef={field.ref}
                    label={label}
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { border: `1px solid ${styles.lightGrey}`, borderRadius: '0.5rem' } }}
                    placeholder={prefix === 'reference' ? 'All' : 'None'}
                    helperText={readOnly ? undefined : field.value && options && !values.some(v => v.Id === field.value.Id)
                      ? 'Saved value is unavailable. Select an active value or clear it.'
                      : prefix === 'reference' ? 'Optional filter; leave empty for all.' : 'Optional; leave empty for none.'}
                  />
                )}
              />
            )}
          />
        );
      })}
    </Box>
  );
}
